import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Users, Plus, Edit2, Trash2, Save, X, User, Camera,
  CheckCircle, AlertCircle, Loader2, ToggleLeft, ToggleRight
} from 'lucide-react';
import api, { API_URL, uploadBarberPhoto } from '../utils/api';
import { EmptyState, InlineAlert, LoadingState, PageHeader, SectionCard } from '../components/ui';
import { useToast } from '../components/ToastProvider';

export default function BarbersManager() {
  const { id } = useParams();
  const toast = useToast();
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBarber, setEditingBarber] = useState(null);
  const [creatingBarber, setCreatingBarber] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') || url.startsWith('data:') ? url : `${API_URL}${url}`;
  };

  const validatePhoto = (file) => {
    if (!file) return false;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione apenas arquivos de imagem.');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no maximo 5MB.');
      return false;
    }
    return true;
  };

  const handleCreatePhotoChange = async (file) => {
    if (!validatePhoto(file)) return;
    setSaving(true);
    setError('');
    try {
      const preview = URL.createObjectURL(file);
      setCreatingBarber(prev => ({ ...prev, photo_preview: preview }));
      const response = await uploadBarberPhoto(id, file);
      setCreatingBarber(prev => ({ ...prev, photo_url: response.data.imageUrl, photo_preview: null }));
      toast.success('Foto enviada.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar foto');
      toast.error(err.response?.data?.error || 'Erro ao enviar foto');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPhotoChange = async (file) => {
    if (!validatePhoto(file) || !editingBarber?.id) return;
    setSaving(true);
    setError('');
    try {
      const preview = URL.createObjectURL(file);
      setEditingBarber(prev => ({ ...prev, photo_preview: preview }));
      const response = await uploadBarberPhoto(id, file, editingBarber.id);
      setEditingBarber(prev => ({ ...prev, photo_url: response.data.imageUrl, photo_preview: null }));
      setBarbers(current => current.map(barber => barber.id === editingBarber.id ? { ...barber, photo_url: response.data.imageUrl } : barber));
      toast.success('Foto do barbeiro atualizada.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar foto');
      toast.error(err.response?.data?.error || 'Erro ao enviar foto');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadBarbers();
  }, [id]);

  const loadBarbers = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/barbers`);
      setBarbers(response.data.barbers || []);
    } catch (err) {
      console.error('Erro ao carregar barbeiros:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/barbershops/${id}/barbers`, {
        name: creatingBarber.name,
        photo_url: creatingBarber.photo_url,
        whatsapp: creatingBarber.whatsapp,
        active: creatingBarber.active
      });

      setBarbers([...barbers, response.data.barber]);
      setCreatingBarber(false);
      setSuccess('Barbeiro criado com sucesso!');
      toast.success('Barbeiro criado com sucesso.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar barbeiro');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put(`/barbershops/${id}/barbers/${editingBarber.id}`, {
        name: editingBarber.name,
        photo_url: editingBarber.photo_url,
        whatsapp: editingBarber.whatsapp,
        active: editingBarber.active
      });

      setBarbers(barbers.map(b => 
        b.id === editingBarber.id ? editingBarber : b
      ));
      setEditingBarber(null);
      setSuccess('Barbeiro atualizado com sucesso!');
      toast.success('Barbeiro atualizado com sucesso.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar barbeiro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (barberId) => {
    if (!confirm('Tem certeza que deseja desativar este barbeiro?')) return;

    try {
      await api.delete(`/barbershops/${id}/barbers/${barberId}`);
      setBarbers(barbers.map(b => 
        b.id === barberId ? { ...b, active: false } : b
      ));
      setSuccess('Barbeiro desativado com sucesso!');
      toast.success('Barbeiro desativado com sucesso.');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao desativar barbeiro';
      if (errorMsg.includes('agendamentos futuros')) {
        setError(errorMsg);
      } else {
        setError(errorMsg);
      }
    }
  };

const toggleActive = async (barber) => {
    try {
      await api.put(`/barbershops/${id}/barbers/${barber.id}`, {
        active: !barber.active
      });
      setBarbers(barbers.map(b => 
        b.id === barber.id ? { ...b, active: !b.active } : b
      ));
      setSuccess(`Barbeiro ${!barber.active ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  const startEdit = (barber) => {
    setEditingBarber({...barber});
  };

  const cancelEdit = () => {
    setEditingBarber(null);
    setCreatingBarber(false);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return <LoadingState label="Carregando barbeiros..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Equipe"
        title="Gerenciar barbeiros"
        description="Controle profissionais, WhatsApp, foto e disponibilidade de cada barbeiro."
        icon={Users}
        action={
          <button
            onClick={() => setCreatingBarber({
              name: '',
              photo_url: '',
              active: true
            })}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo barbeiro
          </button>
        }
      />

      <SectionCard
        title="Profissionais"
        description={`${barbers.length} profissional${barbers.length !== 1 ? 'is' : ''} cadastrado${barbers.length !== 1 ? 's' : ''}.`}
        icon={Users}
      >
          {success && <InlineAlert type='success' className='mb-4'>{success}</InlineAlert>}
          {error && <InlineAlert type='error' className='mb-4'>{error}</InlineAlert>}

          {/* Lista de Barbeiros */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {barbers.map((barber) => (
              <div key={barber.id} className="card card-hover">
                {editingBarber?.id === barber.id ? (
                  // Formulário de Edição
                  <form onSubmit={handleUpdate} className="p-4 space-y-3">
                    <div>
                      <label className="label text-xs">Nome</label>
                      <input
                        type="text"
                        required
                        className="input text-sm"
                        value={editingBarber.name}
                        onChange={(e) => setEditingBarber({...editingBarber, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="label text-xs">Foto do barbeiro</label>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-2xl bg-dark-700 overflow-hidden border border-white/10 flex items-center justify-center">
                          {editingBarber.photo_preview || editingBarber.photo_url ? (
                            <img src={getImageUrl(editingBarber.photo_preview || editingBarber.photo_url)} alt={editingBarber.name || 'Barbeiro'} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-7 h-7 text-gray-500" />
                          )}
                        </div>
                        <label className="btn-secondary text-xs px-3 py-2 cursor-pointer">
                          <Camera className="w-4 h-4 mr-2" />
                          Escolher foto
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleEditPhotoChange(e.target.files?.[0])}
                          />
                        </label>
                        {editingBarber.photo_url && (
                          <button
                            type="button"
                            className="btn-ghost text-xs text-red-300 px-3 py-2"
                            onClick={() => setEditingBarber({ ...editingBarber, photo_url: '', photo_preview: null })}
                          >
                            Remover
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="label text-xs">WhatsApp</label>
                      <input
                        type="text"
                        placeholder="Ex: 5511999999999"
                        className="input text-sm"
                        value={editingBarber.whatsapp || ''}
                        onChange={(e) => setEditingBarber({...editingBarber, whatsapp: e.target.value})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-400">Status:</label>
                      <button
                        type="button"
                        onClick={() => setEditingBarber({...editingBarber, active: !editingBarber.active})}
                        className="flex items-center gap-2"
                      >
                        {editingBarber.active ? (
                          <ToggleRight className="w-6 h-6 text-green-400" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-gray-600" />
                        )}
                        <span className="text-xs text-white">
                          {editingBarber.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="btn-primary text-sm flex-1"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          <Save className="w-4 h-4 mx-auto" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn-ghost text-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                ) : (
                  // Visualização Normal
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                        {barber.photo_url ? (
                          <img 
                            src={getImageUrl(barber.photo_url)}
                            alt={barber.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white truncate">{barber.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            barber.active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {barber.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">{barber.total_appointments || 0} agendamentos</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => startEdit(barber)}
                        className="btn-ghost text-sm flex-1"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </button>
                      <button
                        onClick={() => toggleActive(barber)}
                        className={`btn-ghost text-sm flex-1 ${
                          barber.active ? 'text-orange-400' : 'text-green-400'
                        }`}
                      >
                        {barber.active ? (
                          <>
                            <ToggleLeft className="w-4 h-4 mr-1" />
                            Desativar
                          </>
                        ) : (
                          <>
                            <ToggleRight className="w-4 h-4 mr-1" />
                            Ativar
                          </>
                        )}
                      </button>
                      {!barber.active && (
                        <button
                          onClick={() => handleDelete(barber.id)}
                          className="btn-ghost text-sm text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {barbers.length === 0 && !creatingBarber && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Nenhum barbeiro cadastrado</h3>
              <p className="text-gray-400 mb-4">Comece adicionando seu primeiro barbeiro</p>
              <button
                onClick={() => setCreatingBarber({
                  name: '',
                  photo_url: '',
                  active: true
                })}
                className="btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adicionar Barbeiro
              </button>
            </div>
          )}
      </SectionCard>

      {/* Modal/Criação de Novo Barbeiro */}
      {creatingBarber && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Novo Barbeiro</h3>
              
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="label">Nome do Barbeiro</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Carlos Silva"
                    className="input"
                    value={creatingBarber.name}
                    onChange={(e) => setCreatingBarber({...creatingBarber, name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="label flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Foto do barbeiro
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-2xl bg-dark-700 overflow-hidden border border-white/10 flex items-center justify-center">
                      {creatingBarber.photo_preview || creatingBarber.photo_url ? (
                        <img src={getImageUrl(creatingBarber.photo_preview || creatingBarber.photo_url)} alt={creatingBarber.name || 'Barbeiro'} className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-8 h-8 text-gray-500" />
                      )}
                    </div>
                    <label className="btn-secondary cursor-pointer">
                      <Camera className="w-4 h-4 mr-2" />
                      Escolher foto
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCreatePhotoChange(e.target.files?.[0])}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Envie uma imagem do dispositivo. Maximo 5MB.
                  </p>
                </div>
                <div>
                  <label className="label">WhatsApp (opcional)</label>
                  <input
                    type="text"
                    placeholder="Ex: 5511999999999"
                    className="input"
                    value={creatingBarber.whatsapp || ''}
                    onChange={(e) => setCreatingBarber({...creatingBarber, whatsapp: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Número com DDD e código do país (ex: 5511999999999)
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400">Status inicial:</label>
                  <button
                    type="button"
                    onClick={() => setCreatingBarber({...creatingBarber, active: !creatingBarber.active})}
                    className="flex items-center gap-2"
                  >
                    {creatingBarber.active ? (
                      <ToggleRight className="w-6 h-6 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-gray-600" />
                    )}
                    <span className="text-sm text-white">
                      {creatingBarber.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </button>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary flex-1"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5 mr-2" />
                        Criar Barbeiro
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="btn-ghost"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
